import { useState } from 'react';

import { InputGroupInput } from '@/_shared/components/ui/input-group';

import { FusedSelect } from './FusedSelect';
import { TIME_UNITS } from './lib/operators';
import type { RelativeWindow, TimeUnit } from './types';

/** The "in the last [N] [days/weeks/months]" control — a chrome-less number input + a unit Select, both fused
 *  into the parent InputGroup. An empty interval is committed as `null` (unset) — never `NaN` — and the chosen
 *  unit is remembered locally while the interval is blank, so picking a unit before typing a number isn't lost
 *  and never commits a phantom interval. */
export function RelativeWindowValue({
  value,
  onChange,
  ariaLabel,
}: {
  value: RelativeWindow | null;
  onChange: (window: RelativeWindow | null) => void;
  ariaLabel: string;
}) {
  // Unit is held locally so clearing the interval (→ value null) doesn't lose the chosen unit. Kept in sync
  // with an external value change via the guarded render-time set (React's "adjust state on prop change").
  const [unit, setUnit] = useState<TimeUnit>(value?.unit ?? 'days');
  if (value && value.unit !== unit) setUnit(value.unit);

  const commit = (interval: number | null, u: TimeUnit) => onChange(interval == null ? null : { interval, unit: u });

  const interval = value && Number.isFinite(value.interval) ? value.interval : null;

  return (
    <>
      <InputGroupInput
        aria-label={`${ariaLabel} interval`}
        type="number"
        min={1}
        inputMode="numeric"
        placeholder="N"
        value={interval ?? ''}
        onChange={(e) =>
          // Empty → unset (null), never NaN; a typed value floors at 1.
          commit(e.target.value === '' ? null : Math.max(1, Math.floor(Number(e.target.value))), unit)
        }
        className="field-sizing-content min-w-12 flex-none px-2.5 text-center"
      />
      <FusedSelect
        options={TIME_UNITS}
        value={unit}
        onChange={(u) => {
          setUnit(u as TimeUnit);
          // Only re-commit if an interval already exists — a unit pick on an empty window stays unset.
          commit(interval, u as TimeUnit);
        }}
        ariaLabel={`${ariaLabel} unit`}
      />
    </>
  );
}
